// ClientLayout - Layout completo para el área de clientes
import { type ReactNode, useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import {
  DocumentTextIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  InboxArrowDownIcon,
} from '@heroicons/react/24/outline';
import { useClienteIncidencias } from '@/hooks/useIncidencias';

interface ClientLayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: typeof DocumentTextIcon;
  description: string;
}

const navigation: NavItem[] = [
  {
    name: 'Mi Trámite',
    href: '/clientes/tramite',
    icon: ClipboardDocumentListIcon,
    description: 'Estado de tu trámite y documentación',
  },
  {
    name: 'Solicitudes',
    href: '/clientes/solicitudes',
    icon: InboxArrowDownIcon,
    description: 'Solicitudes de tu abogado',
  },
  {
    name: 'Mis Contratos',
    href: '/clientes/contratos',
    icon: DocumentTextIcon,
    description: 'Ver y descargar tus contratos',
  },
  {
    name: 'Suscripciones',
    href: '/clientes/suscripciones',
    icon: CreditCardIcon,
    description: 'Gestionar tus suscripciones activas',
  },
  {
    name: 'Facturas',
    href: '/clientes/facturas',
    icon: DocumentDuplicateIcon,
    description: 'Consultar y descargar facturas',
  },
  {
    name: 'Citas',
    href: '/clientes/citas',
    icon: CalendarDaysIcon,
    description: 'Solicitar cita con tu abogado',
  },
  {
    name: 'Incidencias',
    href: '/clientes/incidencias',
    icon: ExclamationTriangleIcon,
    description: 'Reportar y seguir problemas o consultas',
  },
  {
    name: 'Actividad',
    href: '/clientes/actividad',
    icon: ClockIcon,
    description: 'Historial de eventos de tu caso',
  },
  {
    name: 'Mi Perfil',
    href: '/clientes/perfil',
    icon: UserCircleIcon,
    description: 'Actualizar tu información personal',
  },
  {
    name: 'Ayuda',
    href: '/clientes/ayuda',
    icon: QuestionMarkCircleIcon,
    description: 'Preguntas frecuentes y soporte',
  },
];

function SidebarNav({ items, currentPath, onNavigate, badges }: { items: NavItem[]; currentPath: string; onNavigate?: () => void; badges?: Record<string, number> }) {
  return (
    <nav aria-label="Portal de cliente">
      <ul className="space-y-1">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = currentPath === item.href;
          const badge = badges?.[item.href] ?? 0;
          return (
            <li key={item.href}>
              <Link
                to={item.href}
                onClick={onNavigate}
                aria-current={isActive ? 'page' : undefined}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {item.name}
                    {badge > 0 && (
                      <span
                        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-red-500 text-white'
                        }`}
                        aria-label={`${badge} pendientes`}
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </div>
                  <div className={`text-xs ${isActive ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'}`}>
                    {item.description}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function UserSection({ user, onLogout, loggingOut }: {
  user: { full_name?: string | null; email?: string } | null;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  return (
    <div className="border-t border-gray-200 dark:border-gray-800 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white font-semibold" aria-hidden="true">
          {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {user?.full_name || user?.email}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onLogout}
        disabled={loggingOut}
        className="w-full"
      >
        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" aria-hidden="true" />
        {loggingOut ? 'Cerrando...' : 'Cerrar sesión'}
      </Button>
    </div>
  );
}

export function ClientLayout({ children }: ClientLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Badge: incidencias abiertas (safe — loading returns empty array)
  const { incidencias: allInc, loading: incLoading } = useClienteIncidencias();
  const openIncCount = incLoading ? 0 : allInc.filter((i) => !['closed', 'resolved'].includes(i.estado)).length;
  const navBadges: Record<string, number> = {
    '/clientes/incidencias': openIncCount,
  };

  // Cerrar sidebar al cambiar de ruta
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Cerrar sidebar con Escape
  useEffect(() => {
    if (!sidebarOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await logout();
    } catch {
      // Si falla el logout en servidor, limpiar sesión local igualmente
    } finally {
      setLoggingOut(false);
      navigate('/clientes');
    }
  }, [logout, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
      >
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />

        {/* Sidebar panel */}
        <div className="fixed inset-y-0 left-0 flex w-full max-w-xs flex-col bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <img src="/assets/migro-logo.png" alt="Migro" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-primary">Portal Cliente</span>
            </div>
            <div className="flex items-center gap-1">
              <ThemeToggle variant="icon" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
                aria-label="Cerrar menú"
              >
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <SidebarNav
              items={navigation}
              currentPath={location.pathname}
              onNavigate={() => setSidebarOpen(false)}
              badges={navBadges}
            />
          </div>

          <UserSection user={user} onLogout={handleLogout} loggingOut={loggingOut} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <img src="/assets/migro-logo.png" alt="Migro" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-primary">Portal Cliente</span>
            </div>
            <ThemeToggle variant="icon" />
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <SidebarNav items={navigation} currentPath={location.pathname} badges={navBadges} />
          </div>

          <UserSection user={user} onLogout={handleLogout} loggingOut={loggingOut} />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Skip link */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-white focus:text-primary"
        >
          Saltar al contenido principal
        </a>

        {/* Mobile header */}
        <div className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-white dark:bg-gray-900 px-4 lg:hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú"
            aria-expanded={sidebarOpen}
          >
            <Bars3Icon className="h-6 w-6" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/assets/migro-logo.png" alt="Migro" className="h-6 w-auto" />
            <span className="font-semibold text-primary">Portal Cliente</span>
          </div>
        </div>

        {/* Page content */}
        <main id="main-content" className="p-4 lg:p-8">
          <div className="max-w-5xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
